import React, { useEffect, useMemo, useState } from 'react';

import { Button, Group, Modal, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import AsyncAddPaymentMethod from 'components/payment-methods/AsyncAddPaymentMethod';
import { useAuth } from 'context/auth-context';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { BillingPlanRetrieve, SalesPortalPackageRetrieve } from 'types';

import CheckoutFlowStates from './checkoutFlowStates';
import AthleteInformation, { hasVisibleAthleteInformationFields } from './components/AthleteInformation';
import Confirmation from './components/Confirmation';
import Review from './components/Review';
import SelectPaymentMethod from './components/SelectPaymentMethod';

interface ICheckoutControllerProps {
  packageData: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve;
}

const CheckoutController = ({ packageData, billingPlan }: ICheckoutControllerProps): JSX.Element => {
  const { session } = useAuth();
  const theme = useMantineTheme();
  const { trackSalesPortalPurchaseStarted } = useTracking();

  useEffect(() => {
    trackSalesPortalPurchaseStarted(
      packageData.gym.gymId,
      packageData.packageId,
      packageData.type,
      billingPlan.billingPlanId,
      billingPlan.numberOfInstallments,
      'athlete_app',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData.packageId, billingPlan.billingPlanId]);

  // Spec Item 6 parity: the athlete-side checkout collects the package's configured
  // athlete-information fields (address etc.) first, like the public portal does.
  const paymentEntryState: CheckoutFlowStates = billingPlan.salesPortalRequiresPaymentMethod
    ? 'Select Payment Method'
    : 'Review';
  const [state, setState] = useState<CheckoutFlowStates>(
    hasVisibleAthleteInformationFields(packageData.packagesalesportalconfiguration)
      ? 'Athlete Information'
      : paymentEntryState,
  );
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [noPaymentTaken, setNoPaymentTaken] = useState(false);

  const [checkoutFlowModalOpened, { open: openCheckoutFlowModal, close: closeCheckoutFlowModal }] =
    useDisclosure(false);

  const modalContent = useMemo(() => {
    switch (state) {
      case 'Athlete Information':
        return (
          <AthleteInformation packageData={packageData} onContinue={() => setState(paymentEntryState)} />
        );
      case 'Select Payment Method':
        return (
          <SelectPaymentMethod
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            setState={setState}
            handleClose={closeCheckoutFlowModal}
          />
        );
      case 'Add Payment Method':
        return (
          <AsyncAddPaymentMethod
            athleteId={session?.athlete?.athleteId || ''}
            gymId={session?.gym?.gymId || ''}
            successHandler={(value: string) => {
              setPaymentMethod(value);
              setState('Review');
            }}
            cancelHandler={() => setState('Select Payment Method')}
          />
        );
      case 'Review':
        return (
          <Review
            athleteId={session?.athlete?.athleteId || ''}
            fullName={session?.athlete?.fullName || ''}
            packageData={packageData}
            billingPlan={billingPlan}
            paymentMethod={paymentMethod}
            setState={setState}
            setNoPaymentTaken={setNoPaymentTaken}
            handleClose={closeCheckoutFlowModal}
          />
        );
      case 'Confirmation':
        return <Confirmation packageData={packageData} noPaymentTaken={noPaymentTaken} />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, packageData, billingPlan, closeCheckoutFlowModal, paymentMethod, noPaymentTaken]);

  return (
    <>
      <Group position='center' sx={{ background: theme.colors.gray[0], width: '100%' }} p='lg' mb='md'>
        <Button onClick={openCheckoutFlowModal}>Checkout</Button>
      </Group>
      <Modal
        centered
        opened={checkoutFlowModalOpened}
        onClose={closeCheckoutFlowModal}
        title={
          <Title order={4} color='dimmed'>
            {state}
          </Title>
        }
        size='lg'
      >
        {modalContent}
      </Modal>
    </>
  );
};

export default CheckoutController;
